// Displays a single card for a food grouping.  The card shows the grouping's
// price range and opens a dialog where the user picks which version of the item
// they want.  The selected version is rendered with the regular FoodElement so
// it behaves exactly like an ungrouped item on the marketplace.
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FoodElement from "./FoodElement.jsx";
import DEFAULT_FOOD_IMAGE from "./../../../images/DefaultFoodImage.js";
import {
  getGroupingImage,
  getShopperPrice,
} from "../Functions/FoodGroupingUtils.js";
import BasketItemQuantity from "../Functions/BasketItemQuantity.js";

// Builds the key used to identify a foodItem throughout the marketplace.
const variantKey = (variant) =>
  (variant.item + variant.description + variant.farmName).replace(/\s+/g, "");

export default function GroupedFoodElement({ entry, ...foodElementProps }) {
  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(variantKey(entry.variants[0]));

  const { grouping, variants, minPrice, maxPrice } = entry;

  // The variants are re-created whenever the marketplace items change, so the
  // selection is tracked by key and resolved on each render.  That way the
  // selected card always shows the current quantity for the item.
  const selectedVariant =
    variants.find((variant) => variantKey(variant) === selectedKey) ||
    variants[0];

  const groupingImage = getGroupingImage(grouping, variants);

  // The price each version is listed at for this user, which is the same price
  // its own card shows.
  const variantPrice = (variant) =>
    getShopperPrice(
      variant,
      foodElementProps.userInfo,
      foodElementProps.pickupLocationDetails,
    );

  // How many of a version the user already has in their cart.  Only a user that
  // is signed in has a cart.
  const variantInCart = (variant) =>
    foodElementProps.currentUser
      ? BasketItemQuantity(foodElementProps.userInfo.basketItems, variant)
      : 0;

  const priceRange =
    minPrice === maxPrice
      ? `$${minPrice.toFixed(2)}`
      : `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;

  // The basket handlers locate the item by its index in the displayed items, so
  // the selected variant's real index has to be passed along.
  const selectedIndex = (foodElementProps.displayedFoodItems || []).findIndex(
    (foodItem) => variantKey(foodItem) === variantKey(selectedVariant),
  );

  return (
    <>
      <div
        className="Food-Element Food-Grouping-Element"
        onClick={() => setOpen(true)}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setOpen(true);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="Image-Limited-Box">
          <p className="Marketplace-Limited-Remaining">
            {variants.length} options
          </p>
          <div>
            <img
              className="Food-Image"
              alt={grouping.itemName}
              src={groupingImage}
              referrerPolicy="no-referrer"
            />
          </div>
        </div>
        <div className="Item-Details">
          <span className="Item-Name-Marketplace">{grouping.itemName}</span>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="Item-Price-Marketplace">{priceRange}</div>
          </div>
          <p className="Item-Description-Marketplace">{grouping.category}</p>
        </div>
      </div>

      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        fullWidth
        maxWidth="md"
        aria-labelledby="food-grouping-dialog-title"
      >
        <DialogTitle id="food-grouping-dialog-title">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {grouping.itemName}
            {/* Not everyone knows they can click outside the window to close it
            so they are given a button to close it with. */}
            <IconButton
              aria-label="Close"
              onClick={() => setOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", gap: "24px", paddingBottom: "20px" }}>
            {/* The currently selected version shown as a normal food card. */}
            <div style={{ flex: "0 0 320px", maxWidth: "320px" }}>
              <FoodElement
                {...foodElementProps}
                key={variantKey(selectedVariant)}
                index={selectedIndex}
                row={selectedVariant}
              />
            </div>
            {/* The other versions, cheapest first, shown as image only. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: "10px",
                alignContent: "flex-start",
                flexGrow: 1,
              }}
            >
              {variants.map((variant) => {
                const key = variantKey(variant);
                const selected = key === variantKey(selectedVariant);
                const inCart = variantInCart(variant);
                return (
                  <button
                    key={key}
                    type="button"
                    aria-label={`${variant.item} ${variant.individualDescription} from ${variant.farmName}`}
                    aria-pressed={selected}
                    onClick={() => setSelectedKey(key)}
                    style={{
                      padding: "3px",
                      cursor: "pointer",
                      background: "white",
                      border: selected
                        ? "3px solid rgb(159, 191, 89)"
                        : "1px solid rgb(200, 200, 200)",
                      borderRadius: "4px",
                    }}
                  >
                    <img
                      alt=""
                      src={variant.displayImage || DEFAULT_FOOD_IMAGE}
                      referrerPolicy="no-referrer"
                      style={{
                        display: "block",
                        width: "100%",
                        height: "70px",
                        objectFit: "cover",
                      }}
                    />
                    {/* The price of this version so the user can tell them
                    apart without opening each one. */}
                    <div
                      className="Item-Price-Marketplace"
                      style={{
                        fontSize: "15px",
                        margin: "3px 0px 1px",
                        textAlign: "center",
                      }}
                    >
                      ${variantPrice(variant).toFixed(2)}
                    </div>
                    {/* Only shown when the user already has this version in
                    their cart, so the tiles stay uncluttered otherwise. */}
                    {inCart > 0 && (
                      <p
                        className="Quantities-In-Basket"
                        style={{
                          margin: "0px 0px 2px",
                          textAlign: "center",
                        }}
                      >
                        {inCart} in cart
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
